import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearAds,
  initAds,
  mountAdsHelperFunctionsOnWindow,
  updateAdPlatformForViewport,
} from '@/components/advertisement';
import {
  bumpAdsViewportRevision,
  selectAds,
  setInitializedAdChannel,
} from '@/store/slices/ads';
import { selectIsUserLoading, selectIsUserLoggedIn } from '@/store/slices/user';

import useBreakpoint from './use-breakpoint';

const VIEWPORT_RELOAD_MS = 250;

const useInitAds = () => {
  const { asPath } = useRouter();
  const adsConfig = useSelector(selectAds);
  const isUserLoading = useSelector(selectIsUserLoading);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const { currentBreakpoint, isMobile } = useBreakpoint();
  const dispatch = useDispatch();
  const prevIsMobileRef = useRef<boolean | null>(null);
  const viewportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { adsEnabled, deactivateAdSlots, adChannel, isCreatorProfilePage } =
    adsConfig;

  const canRunAds =
    !isUserLoading && adsEnabled && !deactivateAdSlots && Boolean(adChannel);

  useEffect(() => {
    mountAdsHelperFunctionsOnWindow();
  }, []);

  useEffect(() => {
    if (!canRunAds || !adChannel) {
      return undefined;
    }

    initAds(
      adChannel,
      isMobile,
      currentBreakpoint,
      isCreatorProfilePage,
      isUserLoggedIn,
      () => dispatch(setInitializedAdChannel(adChannel)),
    );

    prevIsMobileRef.current = isMobile;

    return () => {
      if (adsEnabled) {
        dispatch(setInitializedAdChannel(null));
        clearAds();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath, isUserLoading, isUserLoggedIn]);

  useEffect(() => {
    if (!canRunAds || !adChannel) {
      return undefined;
    }

    if (prevIsMobileRef.current === null) {
      prevIsMobileRef.current = isMobile;

      return undefined;
    }

    if (prevIsMobileRef.current === isMobile) {
      return undefined;
    }

    prevIsMobileRef.current = isMobile;

    if (viewportTimerRef.current) {
      clearTimeout(viewportTimerRef.current);
    }

    viewportTimerRef.current = setTimeout(() => {
      updateAdPlatformForViewport(
        adChannel,
        isMobile,
        currentBreakpoint,
        isCreatorProfilePage,
        isUserLoggedIn,
        () => {
          dispatch(setInitializedAdChannel(adChannel));
          dispatch(bumpAdsViewportRevision());
        },
      );
    }, VIEWPORT_RELOAD_MS);

    return () => {
      if (viewportTimerRef.current) {
        clearTimeout(viewportTimerRef.current);
      }
    };
  }, [
    isMobile,
    canRunAds,
    adChannel,
    isCreatorProfilePage,
    isUserLoggedIn,
    dispatch,
    currentBreakpoint,
  ]);
};

export default useInitAds;
